import React, { useEffect, useState } from 'react'
import ExpandableContainer from '../components/ui/expandable-container'
import { NomiNumeriResponseJSON } from '../types/output-data'
import { getNomiNumeriResponse } from '../services/backend-services'
import { extractNomiResponseDataFromJSON } from '../utils/extract-data-from-json'
import SignalsService from '../services/communication/signals.service'
import { ERROR_CAUSE, ERROR_MESSAGE, SIGNAL_EVENT } from '../types/enums'
import PostMessageService, { PostMessageType } from '../services/communication/post-message.service'
import ErrorMessage from '../components/ui/error-message'
import Button from '../components/ui/button'
import Resizer from '../components/ui/resizer'
import Number from '../components/ui/number'

/** Pagina che permette la visualizzazione dei numeri consigliati da giocare (e di altre informazioni ausiliarie) sulla base di un nome proprio di persona
 *
 * @desc La pagina in questione non deve permettere il submit del bottone qual ora l'utente inserisca, come nome proprio di persona, una stringa vuota o degli spazi.
 * */
const NumeriCaldiNomi = () => {
  const [isInvalidInput, setIsInvalidInput] = useState(false)
  const [disableButton, setDisableButton] = useState<boolean>(true)
  const [nameToSearch, setNameToSearch] = useState<string>('')
  const [numberResponse, setNumberResponse] = useState<NomiNumeriResponseJSON>({
    numbers: '',
    day: '',
    talisman: '',
    description: '',
    alternatives: ''
  })

  const [errorCause, setErrorCause] = useState<ERROR_CAUSE>()
  const [showError, setShowError] = useState(false)

  const nomiTestId = 'nomi'
  const showNumber = numberResponse?.numbers !== ''

  const handleBlur = (event: any) => {
    if (event.target.value === '') {
      setIsInvalidInput(true)
    } else {
      setIsInvalidInput(false)
    }
  }

  const handleChangeInput = (e: any) => {
    setShowError(false)
    const value = e.target.value

    // Controlla se l'input contiene spazi
  /*  if (/\s/.test(value)) {
      // mostra errore se il testo contiene spazi e rendi il bottone non attivo
      setIsInvalidInput(true)
      setDisableButton(true)
    } else {*/
      setShowError(false)
      if (value.toString().trim().length > 0) {
        setIsInvalidInput(false)
        setDisableButton(false)
        setNameToSearch(value)
      } else {
        setIsInvalidInput(true)
        setDisableButton(true)
        setNameToSearch('')
      }
    //}
  }

  const handleButtonClick = async () => {
    try {
      SignalsService.fire(SIGNAL_EVENT.showLoader, 'flex')
      const responseJson = await getNomiNumeriResponse(nameToSearch)
      const response = extractNomiResponseDataFromJSON(responseJson)
      setNumberResponse(response)
    } catch (e) {
      if (e instanceof Error) {
        console.error('Numeri Associati ai nomi:', e.message)
        setNumberResponse({
          numbers: '',
          day: '',
          talisman: '',
          description: '',
          alternatives: ''
        })
        setErrorCause(e.cause as ERROR_CAUSE)
        setShowError(true)
      }
    } finally {
      SignalsService.fire(SIGNAL_EVENT.showLoader, 'hidden')
    }
  }

  useEffect(() => {
    if (numberResponse.numbers && numberResponse.numbers.length > 0) {
      PostMessageService.send({
        type: PostMessageType.PLAY_NUMBERS,
        numbers: numberResponse.numbers
      })
    } else {
      PostMessageService.send({
        type: PostMessageType.RESET_NUMBERS
      })
    }
  }, [numberResponse])

  return (
    <Resizer idResizer={nomiTestId}>
      <div className={'w-full h-full text-base py-5 px-[30px] flex flex-col bg-lightOrangeColor'}>
        <form className={'grid grid-cols-1'}>
          <div className={'w-full flex desktop:flex-row flex-col py-5'}>
            <div className={'px-3.7 mobile:w-full flex flex-col h-[60.7px]'}>
              <p>Inserisci il tuo nome</p>
              <input
                id={nomiTestId + '-input'}
                onBlur={handleBlur}
                onChange={handleChangeInput}
                className={`desktop:w-[270px] w-full px-3.5 h-fit ${isInvalidInput ? 'border-borderRed' : ''}`}
              />
            </div>

            <div className={'px-3.7 flex items-end'}>
              <Button
                idButton={nomiTestId}
                buttonText={'calcola il numero'}
                handleClick={handleButtonClick}
                disabled={disableButton}
                className={'mt-2 h-[55px] desktop:w-[340px] items-center flex justify-center'}
              />
            </div>
          </div>
          <ExpandableContainer
            id={nomiTestId}
            expandableCondition={showNumber}
            className={`transition-vertical-spacing duration-1000 ease-in-out overflow-hidden px-3.7 ${!showNumber ? '' : `py-5 mt-2.75`}`}
            tot_padding_y={40}
            tot_margin_y={11}>
            <div className={'flex desktop:flex-row flex-col'}>
              <div className={'flex'}>
                <div className={'px-3.7'}>
                  <Number
                    key={`${nomiTestId}-0`}
                    idNumber={nomiTestId + '-0'}
                    numberTxt={numberResponse?.numbers}
                    customClassName={
                      'h-[70px] w-[70px] mobile:w-auto min-w-14 mobile:flex-grow px-3.7'
                    }
                  />
                </div>
                <div id={'nomi-description'} className={'px-3.7'}>
                  <p>{numberResponse?.description}</p>
                </div>
              </div>
              <div className={'px-3.7 flex flex-col desktop:items-start items-center pt-2'}>
                {numberResponse?.day && (
                  <span id={nomiTestId + '-day'}>
                    <strong>Giorno:</strong> {numberResponse.day}
                  </span>
                )}
                {numberResponse?.talisman && (
                  <span id={nomiTestId + '-talisman'}>
                    <strong>Talismano:</strong> {numberResponse.talisman}
                  </span>
                )}
                {numberResponse?.alternatives && (
                  <span id={nomiTestId + '-alternatives'}>
                    <strong>Varianti:</strong> {numberResponse.alternatives}
                  </span>
                )}
              </div>
            </div>
          </ExpandableContainer>
        </form>
      </div>
      <ErrorMessage
        idError={nomiTestId}
        isVisible={showError}
        errorMessage={
          errorCause === ERROR_CAUSE.EmptyNumber ? ERROR_MESSAGE.msgInvalidName : undefined
        }
      />
    </Resizer>
  )
}

export default NumeriCaldiNomi
